// The whole code is copied from VPC.

/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { DataGrid } from '@tableau/widgets-datagrid';
import { ContentType } from '../../models/ContentType';
import { ListViewGridContentGenerator, ListViewGridContentGeneratorBaseParams } from './ListViewGridContentGenerator';
import { ListViewStyles } from './ListViewStyles';
import { AppState } from '../../../app/state/AppState';
import { requestContentSlice } from '../../actions/RequestContentSliceAction';
import { connect } from 'react-redux';
import { FeatureFlag, FeatureFlagService } from '../../services/FeatureFlagService';
import { DisplaySelection } from '../../models/DisplaySelection';

export interface ListViewStateProps {
  readonly ignoreExtractTaskPriority: boolean;
  readonly displayScheduleDescriptionAsName: boolean;
  readonly inServerArea: boolean;
  readonly searchTerm: string;
  readonly totalForMixedContent: number;
  readonly selectedContentType: string;
  readonly displayBy?: DisplaySelection;
}

export interface ListViewDispatchProps {
  readonly requestContentSlice: (offset: number, count: number, contentType: ContentType) => void;
}

export type ListViewGridContentGeneratorBaseProps = Omit<
  ListViewGridContentGeneratorBaseParams,
  keyof ListViewStateProps | keyof ListViewDispatchProps
>;

// eslint-disable-next-line prettier/prettier
export interface ListViewOwnProps extends ListViewGridContentGeneratorBaseProps { }

export type ListViewProps = ListViewOwnProps & ListViewStateProps & ListViewDispatchProps;

export class ListView extends React.Component<ListViewProps, { isInitialRenderDone: boolean }> {
  private listViewRef = React.createRef<HTMLDivElement>();
  private contentGenerator: ListViewGridContentGenerator | undefined;

  constructor(props: ListViewProps) {
    super(props);
    this.state = { isInitialRenderDone: false };
  }

  public componentDidMount() {
    this.setState({ isInitialRenderDone: true });
  }

  public render(): JSX.Element {
    if (!this.contentGenerator) {
      this.contentGenerator = new ListViewGridContentGenerator(this.props);
    } else {
      this.contentGenerator.generateContent(this.props);
    }

    if (this.listViewRef.current) {
      let processedColumns = 0;
      const favoriteToggleColumnWidth = 100;
      const alignedColumns = 2;
      const alignedColumnWidth = this.listViewRef.current.clientWidth / 5;
      const usedWidth = alignedColumnWidth * alignedColumns;

      for (let i = 0; i < this.contentGenerator.cols.length; i++) {
        this.contentGenerator.cols[i].growToFit = false;
      }

      if (FeatureFlagService.instance.getValue(FeatureFlag.offspringPostMVP224)) {
        this.contentGenerator.cols[0].preferredWidth = alignedColumnWidth - favoriteToggleColumnWidth;
        this.contentGenerator.cols[1].preferredWidth = favoriteToggleColumnWidth;
        this.contentGenerator.cols[2].preferredWidth = alignedColumnWidth;
        processedColumns = 3;
      } else {
        this.contentGenerator.cols[0].preferredWidth = alignedColumnWidth;
        this.contentGenerator.cols[1].preferredWidth = alignedColumnWidth;
        processedColumns = 2;
      }

      const restWidth = this.listViewRef.current.clientWidth - usedWidth;
      const remainingColumnWidth = restWidth / (this.contentGenerator.cols.length - alignedColumns);
      for (let i = processedColumns; i < this.contentGenerator.cols.length; i++) {
        this.contentGenerator.cols[i].preferredWidth = remainingColumnWidth;
      }
    }

    const props: DataGrid.Props = {
      cols: this.contentGenerator.cols,
      rowStore: this.contentGenerator.rowStore(),
      config: {
        colsPerChunk: this.contentGenerator.cols.length,
        rememberLastScrollPos: true,
        // For empty list view, make headers wide enough so text fits
        ...(this.props.items.length === 0 && { minimumColumnWidth: 65 }),
        rowOuterHeight: ListViewStyles.LIST_VIEW_ROW_OUTER_HEIGHT,
      },
      showVerticalGridLines: false,
      showHorizontalGridLines: true,
      showRowBanding: false,
    };

    const classNames = ['tb-spaces-list-view', ListViewStyles.ListView];
    if (props.cols.length > 0) {
      classNames.push(ListViewStyles.DataGridBodyTopBorder);
    }

    return (
      <div className={classNames.join(' ')} data-tb-test-id="tb-list-view" ref={this.listViewRef}>
        <DataGrid {...props} />
      </div>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  ignoreExtractTaskPriority: true,
  displayScheduleDescriptionAsName: false,
  inServerArea: true,
  searchResults: state.singleContentType?.searchResults,
  searchTerm: state.search.searchTerm,
  totalForMixedContent: state.search.totalCount,
  selectedContentType: state.search.selectedContentTypeFilterValue,
  displayBy: state.search.displayBy,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      requestContentSlice,
    },
    dispatch
  );

export const ConnectedListView: React.ComponentClass<ListViewOwnProps> = connect(mapStateToProps, mapDispatchToProps)(ListView);
